import { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import "./Dashboard.css";
import dataService from "../services/dataService";
import { formatCurrency } from "../utils/formatCurrency";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Package
} from "lucide-react";

function Dashboard() {
  const [summary, setSummary] = useState({ totalRevenue: 0, totalExpenses: 0, netProfit: 0, inventoryCount: 0 });
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [summaryRes, salesRes] = await Promise.all([
          dataService.getReportSummary(),
          dataService.getSalesReport()
        ]);
        setSummary(summaryRes.data.data);
        setChartData(salesRes.data.data);
      } catch (error) {
        console.error("Failed to fetch dashboard data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p>Welcome to your ERP system overview</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Total Sales</span>
            <h2 className="stat-value">{formatCurrency(summary.totalRevenue)}</h2>
            <span className="stat-growth positive">↗ Live</span>
          </div>
          <div className="stat-icon sales">
            <TrendingUp size={24} color="#00A859" />
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Total Expenses</span>
            <h2 className="stat-value">{formatCurrency(summary.totalExpenses)}</h2>
            <span className="stat-growth negative">↘ Live</span>
          </div>
          <div className="stat-icon purchases">
            <TrendingDown size={24} color="#2563EB" />
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Net Profit</span>
            <h2 className="stat-value">{formatCurrency(summary.netProfit)}</h2>
            <span className="stat-growth positive">↗ Live</span>
          </div>
          <div className="stat-icon inventory">
            <DollarSign size={24} color="#8B5CF6" />
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Inventory Items</span>
            <h2 className="stat-value">{summary.inventoryCount || 0}</h2>
            <span className="stat-growth positive">↗ Live</span>
          </div>
          <div className="stat-icon outstanding">
            <Package size={24} color="#EA580C" />
          </div>
        </div>
      </div>

      <div className="dashboard-charts">
        <div className="chart-card">
          <div className="chart-header">
            <h3>Sales Trend</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            {loading ? (
              <p>Loading...</p>
            ) : (
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
                <Line
                  type="monotone"
                  dataKey="sales"
                  stroke="#00A859"
                  strokeWidth={3}
                  dot={{ r: 4, fill: '#00A859' }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>Profit Overview</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            {loading ? (
              <p>Loading...</p>
            ) : (
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
                <Bar dataKey="profit" fill="#2563EB" radius={[4, 4, 0, 0]} />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
